/**
 * Sync journal coparenting serveur ↔ local — Phase 4, incrément 4.10.1.
 *
 * Première sub-entité **coparenting** synchronisée. Étend ADR 0009
 * (server-wins) au champ `data.journal` des foyers coparenting.
 *
 * Symétrique de messages-sync (4.8) sauf :
 *   - Filtre `home.mode === 'coparenting'` au lieu de `'classic'`.
 *   - Pas de filtre media (le journal est texte-only par design).
 *
 * Idempotent, multi-homes tolérant, déclenché en parallèle des
 * autres sub-syncs par `homes-sync.ts`.
 */
import * as journalApi from '../api/journal.js';
import { ApiError } from '../api/client.js';
import {
  applyJournalSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerJournalEntryPayload,
} from './homes.js';
import type { CoparentingHomeData, JournalEntry } from '@nestor/shared-types';

export interface JournalSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: JournalSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getJournalSyncStatus(): JournalSyncStatus {
  return status;
}

/**
 * Synchronise le journal pour tous les foyers UUID coparenting.
 * Idempotent.
 */
export async function syncJournalForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'coparenting') continue;

    const data = home.data as CoparentingHomeData;

    // 1. Migration POST des entries locales pré-auth.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const localOnly = data.journal.filter((e) => !isServerHomeId(e.id));
    for (const entry of localOnly) {
      try {
        const result = await journalApi.createJournalEntry(
          home.id,
          buildCreateInput(entry),
          entry.id,
        );
        if (result.kind === 'sent') {
          idMap.set(entry.id, result.data.entry.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[journal-sync] migration POST échouée', {
          homeId: home.id,
          entryId: entry.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverEntries = await journalApi.listJournalEntries(home.id);
      const payload: ServerJournalEntryPayload[] = serverEntries.map((e) => ({
        id: e.id,
        text: e.text,
        createdAt: e.createdAt,
      }));
      applyJournalSyncResult(home.id, { idMap, serverEntries: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyJournalSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[journal-sync] fetch /journal échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(entry: JournalEntry): journalApi.CreateJournalEntryInput {
  return { text: entry.text };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetJournalSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
